// Dead-property storage for PROPPATCH (browserdav-nephele-parity Phase 3).
// Keyed by resource path in IndexedDB (in-memory in Node conformance runs):
// survives reload, invalidated on DELETE, carried along on MOVE. Properties
// are stored as { "namespaceURI|localName": { xmlns, name, value } } where
// value is the raw inner XML of the property element.

import { createKVStore } from './persistence.js'

export class DeadProperties {
    constructor() {
        this._store = createKVStore('BrowserDAV-webdav-state', 'dead-properties')
    }

    propKey(xmlns, name) {
        return `${xmlns || 'DAV:'}|${name}`
    }

    async get(path) {
        return (await this._store.get(path)) || {}
    }

    async set(path, props) {
        const existing = await this.get(path)
        const merged = { ...existing, ...props }
        await this._store.set(path, merged)
    }

    async removeProps(path, keys) {
        const existing = await this.get(path)
        for (const key of keys) delete existing[key]
        if (Object.keys(existing).length === 0) {
            await this._store.remove(path)
        } else {
            await this._store.set(path, existing)
        }
    }

    /** Drop all properties at path and below (DELETE). */
    async invalidateTree(path) {
        for (const [key] of await this._store.entries()) {
            if (key === path || key.startsWith(path + '/')) {
                await this._store.remove(key)
            }
        }
    }

    /** Re-key properties from oldPath to newPath, recursively (MOVE). */
    async moveTree(oldPath, newPath) {
        for (const [key, value] of await this._store.entries()) {
            if (key === oldPath || key.startsWith(oldPath + '/')) {
                const rekeyed = newPath + key.slice(oldPath.length)
                await this._store.set(rekeyed, value)
                await this._store.remove(key)
            }
        }
    }

    /** Duplicate properties from srcPath to destPath, recursively (COPY). */
    async copyTree(srcPath, destPath) {
        for (const [key, value] of await this._store.entries()) {
            if (key === srcPath || key.startsWith(srcPath + '/')) {
                await this._store.set(destPath + key.slice(srcPath.length), { ...value })
            }
        }
    }
}
